import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AnimatedSection, SEO } from '~/components/common';
import { signUpURL } from '~/utils/constants/env';

const Pricing: React.FC = () => {
  //#region Declare Hook
  const { t } = useTranslation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleToggleFaq = (index: number) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };
  //#endregion Handle Function

  const freeFeatures = ['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6'];
  const proFeatures = ['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6', 'feature7', 'feature8'];
  const faqs = [1, 2, 3, 4];

  return (
    <div className='min-h-screen bg-lumi-dark-300 text-white'>
      <SEO type='pricing' />
      {/* Hero */}
      <section className='pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden'>
        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-lumi-700/15 blur-[100px] pointer-events-none' />
        <AnimatedSection animation='slide-up' className='relative z-10 max-w-3xl mx-auto'>
          <h1 className='text-4xl sm:text-5xl font-bold text-white mb-6'>{t('pricing.hero.title')}</h1>
          <p className='text-white/60 text-lg leading-relaxed'>{t('pricing.hero.subtitle')}</p>
        </AnimatedSection>
      </section>

      {/* Pricing Cards */}
      <section className='py-8 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8'>
          {/* Free Plan */}
          <AnimatedSection animation='slide-right' className='p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col'>
            <div className='mb-6'>
              <h3 className='text-white font-bold text-2xl mb-2'>{t('pricing.free.title')}</h3>
              <p className='text-white/50 text-sm mb-6'>{t('pricing.free.description')}</p>
              <div className='flex items-end gap-2'>
                <span className='text-5xl font-bold text-white'>{t('pricing.free.price')}</span>
                <span className='text-white/40 mb-2'>{t('common.monthly')}</span>
              </div>
            </div>
            <ul className='space-y-3 mb-8 flex-1'>
              {freeFeatures.map((key) => (
                <li key={key} className='flex items-start gap-3'>
                  <svg className='w-5 h-5 text-white/40 flex-shrink-0 mt-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                  </svg>
                  <span className='text-white/60 text-sm'>{t(`pricing.free.${key}`)}</span>
                </li>
              ))}
            </ul>
            <a
              href={signUpURL}
              className='w-full py-3.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-center transition-all duration-200'
            >
              {t('pricing.free.cta')}
            </a>
          </AnimatedSection>

          {/* Pro Plan */}
          <AnimatedSection animation='slide-left' delay={150} className='p-8 rounded-3xl bg-gradient-to-b from-lumi-700/30 to-lumi-dark-200 border border-lumi-500/50 flex flex-col relative overflow-hidden opacity-80'>
            {/* Coming Soon badge */}
            <div className='absolute top-6 right-6 px-3 py-1 rounded-full bg-lumi-700 text-lumi-300 text-xs font-semibold border border-lumi-600'>
              {t('pricing.pro.comingSoon')}
            </div>
            <div className='absolute top-0 right-0 w-[200px] h-[200px] bg-lumi-500/10 blur-[60px] pointer-events-none' />
            <div className='relative z-10 mb-6'>
              <h3 className='text-white font-bold text-2xl mb-2'>{t('pricing.pro.title')}</h3>
              <p className='text-white/50 text-sm mb-6'>{t('pricing.pro.description')}</p>
              <div className='flex items-end gap-2'>
                <span className='text-5xl font-bold text-white'>{t('pricing.pro.price')}</span>
                <span className='text-white/40 mb-2'>{t('common.monthly')}</span>
              </div>
            </div>
            <ul className='space-y-3 mb-8 flex-1 relative z-10'>
              {proFeatures.map((key) => (
                <li key={key} className='flex items-start gap-3'>
                  <div className='w-5 h-5 rounded-full bg-lumi-600 flex items-center justify-center flex-shrink-0 mt-0.5'>
                    <svg className='w-3 h-3 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={3} d='M5 13l4 4L19 7' />
                    </svg>
                  </div>
                  <span className='text-white/70 text-sm'>{t(`pricing.pro.${key}`)}</span>
                </li>
              ))}
            </ul>
            <button
              disabled
              className='relative z-10 w-full py-3.5 rounded-xl bg-lumi-800 text-lumi-500 font-semibold text-center cursor-not-allowed border border-lumi-700'
            >
              🚀 {t('pricing.pro.comingSoon')}
            </button>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-3xl mx-auto'>
          <AnimatedSection animation='slide-up' className='mb-12 text-center'>
            <h2 className='text-3xl font-bold text-white'>{t('pricing.faq.title')}</h2>
          </AnimatedSection>
          <div className='space-y-4'>
            {faqs.map((num, index) => (
              <AnimatedSection
                key={num}
                animation='slide-up'
                delay={index * 80}
                className='rounded-2xl bg-white/5 border border-white/10 overflow-hidden'
              >
                <button
                  onClick={() => handleToggleFaq(index)}
                  className='w-full flex items-center justify-between px-6 py-5 text-left text-white font-medium hover:bg-white/5 transition-colors'
                >
                  <span>{t(`pricing.faq.q${num}`)}</span>
                  <svg
                    className={`w-5 h-5 text-white/40 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}
                    fill='none' viewBox='0 0 24 24' stroke='currentColor'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className='px-6 pb-5'>
                    <p className='text-white/60 text-sm leading-relaxed'>{t(`pricing.faq.a${num}`)}</p>
                  </div>
                )}
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
